import type {
  MemoryClearedPayload,
  MemoryConsolidatedPayload,
  MemoryEntry,
  MemoryForgottenPayload,
  MemoryRelevanceContext,
  MemoryRememberedPayload,
  MemoryScope,
  MemoryStore,
  ScoredEntry,
} from '../types/memory.js';
import type { EventBus } from '../kernel/events.js';
import type { WstackPaths } from '../utils/wstack-paths.js';
import { toErrorMessage } from '../utils/error.js';
import { type MemoryBackend, FileMemoryBackend } from './memory-backend.js';

const MAX_BYTES_TOTAL = 32_000; // ~8K tokens

export interface MemoryStoreOptions {
  paths: WstackPaths;
  /**
   * Optional event bus — when provided, mutations emit events so plugins
   * and other subsystems can react to memory changes.
   */
  events?: EventBus | undefined;
  /**
   * Storage backend. Defaults to FileMemoryBackend when omitted.
   * Plugins can register a custom backend (graph, semantic, vector)
   * via the DI container to override storage behavior.
   */
  backend?: MemoryBackend | undefined;
}

/**
 * Three scopes:
 *   project-agents → <project>/.wrongstack/AGENTS.md     (committed)
 *   project-memory → ~/.wrongstack/projects/<hash>/memory.md   (per-project agent notes)
 *   user-memory    → ~/.wrongstack/memory.md             (global personal memory)
 */
export class DefaultMemoryStore implements MemoryStore {
  private readonly files: Record<MemoryScope, string>;
  private readonly events?: EventBus | undefined;
  private traceId?: string | undefined;
  private readonly backend: MemoryBackend;

  /**
   * Per-scope serialization queue. `remember` / `forget` / `consolidate` /
   * `clear` are read-modify-write against a single file; without a lock,
   * two concurrent calls on the same scope can read the same baseline and
   * the later write silently drops the earlier entry. We chain each
   * mutation onto the prior promise for the same scope so they run in
   * issue order. Different scopes still proceed in parallel.
   *
   * The chain tracks only the last pending write. If a write fails, its
   * error is caught and swallowed so the chain stays alive for subsequent
   * calls. The error is stored in `writeErrors` so callers can learn about
   * it on the next read operation.
   */
  private readonly writeChain = new Map<MemoryScope, Promise<unknown>>();
  /** Last write error per scope — surfaced as warnings on the next readAll(). */
  private readonly writeErrors = new Map<MemoryScope, Error>();

  /**
   * When the global root is a temporary directory (opencode, CI sandboxes),
   * memory files are also mirrored to the project tree so they survive
   * session cleanup. The primary path stays in the temp root for isolation;
   * this backup ensures memory is never lost.
   */
  private readonly persistBackup: boolean;
  private readonly backupDir: string;
  /**
   * Per-scope tracked byte sizes — incremented on `remember()`, decremented on
   * `forget()`, recalculated after `consolidate()`. Eliminates the redundant
   * readAll() call that previously checked the file size after every write.
   */
  private readonly _trackedByteSizes: Partial<Record<MemoryScope, number>> = {};

  /** Result cache for scoreRelevant() — keyed by scope + context hash, TTL 30s. */
  private readonly _scoreCache = new Map<string, { entries: MemoryEntry[]; scored: ScoredEntry[]; expiresAt: number }>();

  /**
   * Per-entry cached lowercase strings — lazily allocated once and reused
   * across scoreRelevant() calls so repeated scoring of the same entries skips
   * re-lowercasing. Keyed by entry object identity; cleared (set to null) on
   * every mutation (remember/forget/consolidate/clear) via invalidateScoreCaches().
   */
  private _cachedLower: WeakMap<MemoryEntry, { textLower: string; tagsLower: string[] }> | null = null;

  constructor(opts: MemoryStoreOptions) {
    this.files = {
      'project-agents': opts.paths.inProjectAgentsFile,
      'project-memory': opts.paths.projectMemory,
      'user-memory': opts.paths.globalMemory,
    };
    this.events = opts.events;
    this.backend = opts.backend ?? new FileMemoryBackend({ paths: opts.paths });

    // Detect temporary global roots: opencode, CI, test sandboxes.
    // When detected, mirror writes to the project's .wrongstack/ dir.
    const root = opts.paths.globalRoot.toLowerCase();
    this.persistBackup = /[/\\](tmp|temp|cache)[/\\]/.test(root);
    this.backupDir = this.persistBackup
      ? opts.paths.inProjectAgentsFile.replace(/AGENTS\.md$/, 'memory-persist')
      : '';
  }

  /** Expose the backend for plugin introspection and advanced queries. */
  getBackend(): MemoryBackend {
    return this.backend;
  }

  private async runSerialized<T>(scope: MemoryScope, work: () => Promise<T>): Promise<T> {
    const prior = this.writeChain.get(scope) ?? Promise.resolve();
    // Chain: catch errors from the prior write, then run the next work item.
    const next = prior
      .catch((err) => {
        this.writeErrors.set(scope, err as Error);
      })
      .then(() => work());
    this.writeChain.set(scope, next as Promise<unknown>);
    try {
      return await next;
    } catch (err) {
      this.writeErrors.set(scope, err as Error);
      throw err;
    } finally {
      if (this.writeChain.get(scope) === next) {
        this.writeChain.delete(scope);
      }
    }
  }

  /**
   * Recalculate the tracked byte size for a scope by re-reading the file and
   * summing the serialized byte length of each line. Called after consolidate()
   * (which modifies the file) to keep the tracker accurate.
   */
  private async _recalcTrackedByteSize(scope: MemoryScope): Promise<number> {
    const raw = await this.backend.readAll(scope, this.files[scope]);
    let total = 0;
    for (const line of raw.split('\n')) {
      if (line.trim()) total += Buffer.byteLength(line, 'utf8');
    }
    this._trackedByteSizes[scope] = total;
    return total;
  }

  async readAll(): Promise<string> {
    const parts: string[] = [];
    for (const scope of ['project-agents', 'project-memory', 'user-memory'] as MemoryScope[]) {
      const writeErr = this.writeErrors.get(scope);
      if (writeErr) {
        parts.push(`> ⚠️ Memory write error (${labelOf(scope)}): ${writeErr.message}`);
      }
      const t0 = Date.now();
      const filePath = this.files[scope];
      try {
        const body = await this.backend.readAll(scope, filePath);
        const dur = Date.now() - t0;
        this.events?.emit('storage.read', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'readAll',
          outcome: 'success',
          durationMs: dur,
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
        if (body.trim()) parts.push(`## ${labelOf(scope)}\n\n${body.trim()}`);
      } catch (err) {
        const dur = Date.now() - t0;
        this.events?.emit('storage.read', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'readAll',
          outcome: 'failure',
          durationMs: dur,
          error: toErrorMessage(err),
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
        throw err;
      }
    }
    return parts.join('\n\n');
  }

  async read(scope: MemoryScope): Promise<string> {
    const t0 = Date.now();
    const filePath = this.files[scope];
    try {
      const body = await this.backend.readAll(scope, filePath);
      const dur = Date.now() - t0;
      this.events?.emit('storage.read', {
        sessionId: '~memory~',
        store: 'memory',
        filePath,
        operation: 'read',
        outcome: 'success',
        durationMs: dur,
        ...(this.traceId !== undefined && { traceId: this.traceId }),
      });
      return body;
    } catch (err) {
      const dur = Date.now() - t0;
      this.events?.emit('storage.read', {
        sessionId: '~memory~',
        store: 'memory',
        filePath,
        operation: 'read',
        outcome: 'failure',
        durationMs: dur,
        error: toErrorMessage(err),
        ...(this.traceId !== undefined && { traceId: this.traceId }),
      });
      throw err;
    }
  }

  /**
   * List entries from a scope, newest first. Delegates to the backend
   * so graph/semantic backends can return enriched or filtered results.
   */
  async list(scope: MemoryScope = 'project-memory', limit?: number): Promise<MemoryEntry[]> {
    return this.backend.list(scope, this.files[scope], limit);
  }

  /**
   * Find memories related to the given text via graph traversal.
   * Falls back to content search when no graph backend is available.
   */
  async findRelated(text: string, scope: MemoryScope = 'project-memory', limit = 5): Promise<MemoryEntry[]> {
    if (this.backend.findRelated) {
      return this.backend.findRelated(scope, this.files[scope], text, limit);
    }
    return this.search(text, scope, limit);
  }

  async search(query: string, scope: MemoryScope = 'project-memory', limit?: number): Promise<MemoryEntry[]> {
    return this.backend.search(scope, query, this.files[scope], limit);
  }

  async remember(
    text: string,
    scope: MemoryScope = 'project-memory',
    metadata?: Omit<Partial<MemoryEntry>, 'scope' | 'text' | 'ts'>,
  ): Promise<void> {
    const ts = new Date().toISOString();
    return this.runSerialized(scope, async () => {
      const entry: MemoryEntry = { scope, text, ts, ...metadata };
      const filePath = this.files[scope];
      const t0 = Date.now();
      try {
        await this.backend.remember(scope, entry, filePath);
        // Invalidate the relevance caches — entries have changed.
        this.invalidateScoreCaches();
        const dur = Date.now() - t0;
        this.events?.emit('storage.write', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'remember',
          outcome: 'success',
          durationMs: dur,
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
      } catch (err) {
        const dur = Date.now() - t0;
        this.events?.emit('storage.write', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'remember',
          outcome: 'failure',
          durationMs: dur,
          error: toErrorMessage(err),
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
        throw err;
      }

      // Size check — consolidate if the tracked byte size exceeds the cap.
      // Tracked size is updated incrementally; lazily initialized from the file
      // on first mutation if not yet tracked (handles the case where the store was
      // constructed but not yet mutated, or after a restart).
      let trackedSize = this._trackedByteSizes[scope];
      if (trackedSize === undefined) {
        trackedSize = await this._recalcTrackedByteSize(scope);
      }
      if (trackedSize > MAX_BYTES_TOTAL) {
        const removed = await this.backend.consolidate(scope, this.files[scope]);
        if (removed > 0) {
          this.events?.emit('memory.consolidated', {
            scope,
            removed,
          } satisfies MemoryConsolidatedPayload);
          // Recalculate after consolidation so the tracked size stays accurate.
          await this._recalcTrackedByteSize(scope);
        }
      }

      // Increment the tracked byte size for this scope.
      this._trackedByteSizes[scope] = (this._trackedByteSizes[scope] ?? 0) + Buffer.byteLength(JSON.stringify(entry), 'utf8');

      // Mirror to persistent backup when running in a temp sandbox.
      await this.mirrorBackup(scope);

      this.events?.emit('memory.remembered', {
        scope,
        text,
        ts,
        type: entry.type,
        tags: entry.tags,
        priority: entry.priority,
      } satisfies MemoryRememberedPayload);
    });
  }

  /**
   * Drop the relevance caches after a mutation. Both the per-context score
   * cache and the per-entry lowercase cache are keyed on entry objects that a
   * mutation invalidates, so they must be cleared together when entries change.
   */
  private invalidateScoreCaches(): void {
    this._scoreCache.clear();
    this._cachedLower = null;
  }

  /**
   * Score and rank memories by relevance to the current context.
   * Returns entries with score >= MIN_RELEVANCE_SCORE, sorted highest first.
   */
  async scoreRelevant(
    ctx: MemoryRelevanceContext,
    scope: MemoryScope = 'project-memory',
    limit = 8,
  ): Promise<ScoredEntry[]> {
    const all = await this.list(scope);
    if (all.length === 0) return [];

    // Build a context fingerprint for cache lookup.
    const ctxHash =
      `${scope}|${ctx.currentTask}|${(ctx.activeSkills ?? []).join(',')}|${(ctx.toolNames ?? []).join(',')}`;
    const now = Date.now();
    const TTL_MS = 30_000; // 30-second cache window

    // Check the TTL cache first — return cached scored results if the same entries
    // and context were scored within the last 30 seconds.
    const cached = this._scoreCache.get(ctxHash);
    if (cached && cached.expiresAt > now && cached.entries === all) {
      return cached.scored.slice(0, Math.min(limit, 15));
    }

    const taskWords = ctx.currentTask.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
    const skillWords = (ctx.activeSkills ?? []).flatMap((s) => s.split('-'));
    const toolWords = (ctx.toolNames ?? []).flatMap((t) => t.toLowerCase().split('_'));

    // Per-entry lowercase caches. Lazily allocated once and reused across
    // scoring passes — the WeakMap is keyed by entry object identity, so its
    // entries stay valid as long as the same entry objects are scored, and
    // GC away once a mutation replaces them with freshly-parsed objects. It is
    // cleared explicitly alongside the score cache on every mutation
    // (remember/forget/consolidate/clear) via invalidateScoreCaches().
    this._cachedLower ??= new WeakMap<MemoryEntry, { textLower: string; tagsLower: string[] }>();
    const lowerCache = this._cachedLower;

    const scored: ScoredEntry[] = [];

    for (const entry of all) {
      let score = 0;
      const reasons: string[] = [];

      // Use cached lowercase strings from the WeakMap; compute and store on first use.
      let cachedLower = lowerCache.get(entry);
      if (!cachedLower) {
        cachedLower = {
          textLower: entry.text.toLowerCase(),
          tagsLower: (entry.tags ?? []).map((t) => t.toLowerCase()),
        };
        lowerCache.set(entry, cachedLower);
      }
      const { textLower, tagsLower } = cachedLower;

      // Word overlap with current task (primary signal)
      let taskHits = 0;
      for (const w of taskWords) {
        if (textLower.includes(w)) { taskHits++; score += 2; }
        if (tagsLower.some((t) => t.includes(w))) { taskHits++; score += 3; }
      }
      if (taskHits > 0) reasons.push(`task match (${taskHits})`);

      // Skill/tool relevance
      let skillHits = 0;
      for (const w of skillWords) {
        if (w.length > 2 && (textLower.includes(w) || tagsLower.some((t) => t.includes(w)))) {
          skillHits++;
        }
      }
      score += skillHits;
      if (skillHits > 0) reasons.push(`skill match (${skillHits})`);

      for (const w of toolWords) {
        if (w.length > 2 && (textLower.includes(w) || tagsLower.some((t) => t.includes(w)))) {
          score += 1;
          reasons.push(`tool mention: ${w}`);
        }
      }

      // Priority boost
      switch (entry.priority) {
        case 'critical': score += 5; reasons.push('critical'); break;
        case 'high':     score += 3; reasons.push('high priority'); break;
        case 'medium':   score += 1; break;
        case 'low':      score -= 2; reasons.push('low priority'); break;
      }

      // Type boost — decisions, conventions, anti-patterns are high-value
      switch (entry.type) {
        case 'decision':    score += 2; reasons.push('decision'); break;
        case 'convention':  score += 2; reasons.push('convention'); break;
        case 'anti_pattern': score += 3; reasons.push('anti-pattern'); break;
        case 'preference':  score += 1; reasons.push('preference'); break;
        case 'reference':   break;
        case 'fact':        break;
      }

      // Recency boost — newer entries get +1, very old get 0
      const ageDays = (now - new Date(entry.ts).getTime()) / (1000 * 60 * 60 * 24);
      if (ageDays < 1) score += 1;
      else if (ageDays > 30) score -= 1;

      // Confidence penalty
      if (entry.confidence !== undefined && entry.confidence < 0.5) {
        score -= 2;
        reasons.push('low confidence');
      }

      // Repetition avoidance — recently accessed entries get slight penalty
      if (entry.lastAccessed) {
        const hoursSinceAccess = (now - new Date(entry.lastAccessed).getTime()) / (1000 * 60 * 60);
        if (hoursSinceAccess < 1) score -= 1;
      }

      if (score > 0) {
        scored.push({
          ...entry,
          score,
          matchReason: reasons.join(', ') || 'keyword match',
        });
      }
    }

    scored.sort((a, b) => b.score - a.score);

    // Filter to entries that meet the minimum relevance threshold.
    // Critical or high-priority entries always pass.
    const threshold = 2;
    const relevant = scored.filter(
      (s) => s.score >= threshold || s.priority === 'critical' || s.priority === 'high',
    );

    // Cache the full scored result for 30 seconds — cleared on every mutation.
    this._scoreCache.set(ctxHash, { entries: all, scored: relevant, expiresAt: now + TTL_MS });

    return relevant.slice(0, Math.min(limit, 15));
  }

  async forget(query: string, scope: MemoryScope = 'project-memory'): Promise<number> {
    return this.runSerialized(scope, async () => {
      const filePath = this.files[scope];
      const t0 = Date.now();
      let removed = 0;
      try {
        removed = await this.backend.forget(scope, query, filePath);
        // Invalidate the relevance caches — entries have changed.
        this.invalidateScoreCaches();
        const dur = Date.now() - t0;
        this.events?.emit('storage.write', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'forget',
          outcome: 'success',
          durationMs: dur,
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
      } catch (err) {
        const dur = Date.now() - t0;
        this.events?.emit('storage.write', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'forget',
          outcome: 'failure',
          durationMs: dur,
          error: toErrorMessage(err),
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
        throw err;
      }
      if (removed > 0) {
        this.events?.emit('memory.forgotten', {
          scope,
          query,
          removed,
        } satisfies MemoryForgottenPayload);
        await this.mirrorBackup(scope);
        await this._recalcTrackedByteSize(scope);
      }
      return removed;
    });
  }

  async consolidate(scope: MemoryScope): Promise<void> {
    return this.runSerialized(scope, async () => {
      const filePath = this.files[scope];
      const t0 = Date.now();
      let removed = 0;
      try {
        removed = await this.backend.consolidate(scope, filePath);
        // Invalidate the relevance caches — entries have changed.
        this.invalidateScoreCaches();
        const dur = Date.now() - t0;
        this.events?.emit('storage.write', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'consolidate',
          outcome: 'success',
          durationMs: dur,
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
      } catch (err) {
        const dur = Date.now() - t0;
        this.events?.emit('storage.write', {
          sessionId: '~memory~',
          store: 'memory',
          filePath,
          operation: 'consolidate',
          outcome: 'failure',
          durationMs: dur,
          error: toErrorMessage(err),
          ...(this.traceId !== undefined && { traceId: this.traceId }),
        });
        throw err;
      }
      if (removed > 0) {
        this.events?.emit('memory.consolidated', {
          scope,
          removed,
        } satisfies MemoryConsolidatedPayload);
        await this.mirrorBackup(scope);
        await this._recalcTrackedByteSize(scope);
      }
    });
  }

  async clear(scope?: MemoryScope): Promise<void> {
    if (scope) {
      await this.runSerialized(scope, async () => {
        const filePath = this.files[scope];
        const t0 = Date.now();
        try {
          await this.backend.clear(scope, filePath);
          // Invalidate the relevance caches — entries have changed.
          this.invalidateScoreCaches();
          const dur = Date.now() - t0;
          this.events?.emit('storage.write', {
            sessionId: '~memory~',
            store: 'memory',
            filePath,
            operation: 'clear',
            outcome: 'success',
            durationMs: dur,
            ...(this.traceId !== undefined && { traceId: this.traceId }),
          });
        } catch (err) {
          const dur = Date.now() - t0;
          this.events?.emit('storage.write', {
            sessionId: '~memory~',
            store: 'memory',
            filePath,
            operation: 'clear',
            outcome: 'failure',
            durationMs: dur,
            error: toErrorMessage(err),
            ...(this.traceId !== undefined && { traceId: this.traceId }),
          });
          throw err;
        }
        this.events?.emit('memory.cleared', { scope } satisfies MemoryClearedPayload);
        await this.mirrorBackup(scope);
        this._trackedByteSizes[scope] = 0;
      });
      return;
    }
    await Promise.all(
      (['project-agents', 'project-memory', 'user-memory'] as MemoryScope[]).map(async (s) =>
        this.runSerialized(s, async () => {
          const filePath = this.files[s];
          const t0 = Date.now();
          try {
            await this.backend.clear(s, filePath);
            const dur = Date.now() - t0;
            this.events?.emit('storage.write', {
              sessionId: '~memory~',
              store: 'memory',
              filePath,
              operation: 'clear',
              outcome: 'success',
              durationMs: dur,
              ...(this.traceId !== undefined && { traceId: this.traceId }),
            });
          } catch (err) {
            const dur = Date.now() - t0;
            this.events?.emit('storage.write', {
              sessionId: '~memory~',
              store: 'memory',
              filePath,
              operation: 'clear',
              outcome: 'failure',
              durationMs: dur,
              error: toErrorMessage(err),
              ...(this.traceId !== undefined && { traceId: this.traceId }),
            });
            throw err;
          }
          this.events?.emit('memory.cleared', { scope: s } satisfies MemoryClearedPayload);
          await this.mirrorBackup(s);
        }),
      ),
    );
    // Every scope was just cleared — drop the relevance caches too.
    this.invalidateScoreCaches();
  }

  /**
   * Return a new MemoryStore proxy that carries `traceId` on every storage
   * event. The original store is left unchanged — callers that need a
   * trace-decorated view (e.g. session-run tools) receive the proxy while
   * the singleton remains trace-free for boot-time use.
   *
   * The proxy implements the full `MemoryStore` interface; all other
   * properties (backend, etc.) are delegated to the original store.
   */
  withTraceId(traceId: string): MemoryStore {
    // Mutate the singleton's traceId so that all subsequent I/O (including
    // calls from tools that captured the original store reference) emits
    // storage events with the correct session trace ID.
    this.traceId = traceId;
    return this;
  }
  private async mirrorBackup(scope: MemoryScope): Promise<void> {
    if (!this.persistBackup || scope === 'project-agents') return;
    try {
      const content = await this.backend.readAll(scope, this.files[scope]);
      const { writeFile, mkdir } = await import('node:fs/promises');
      await mkdir(this.backupDir, { recursive: true });
      await writeFile(`${this.backupDir}/${scope}.md`, content, 'utf8');
    } catch {
      // best-effort
    }
  }
}

function labelOf(scope: MemoryScope): string {
  switch (scope) {
    case 'project-agents':
      return 'Project AGENTS.md';
    case 'project-memory':
      return 'Project memory';
    case 'user-memory':
      return 'User memory';
  }
}
